import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Select,
  Spinner,
  useToast,
} from "../../../design-system";
import {
  getEmployee,
  getEmployeePersonalDetails,
  updateEmployee,
  upsertEmployeePersonalDetails,
} from "../../../helpers/kronaly_helper";
import { COUNTRIES } from "../../../helpers/countries";

const STEPS = [
  { id: "general", label: "Información del empleado" },
  { id: "personal", label: "Información personal" },
  { id: "contacto", label: "Contacto" },
];

const GENDER_OPTIONS = [
  { value: "Masculino", label: "Masculino" },
  { value: "Femenino", label: "Femenino" },
  { value: "Otro", label: "Otro" },
];

const ID_TYPE_OPTIONS = [
  { value: "DNI", label: "DNI" },
  { value: "NIE", label: "NIE" },
  { value: "Pasaporte", label: "Pasaporte" },
  { value: "Otro", label: "Otro" },
];

const PERSONAL_KEYS = [
  "gender",
  "birth_date",
  "nationality",
  "id_document_type",
  "id_document_number",
  "disability_percentage",
  "personal_phone",
  "address_line_1",
  "address_line_2",
  "city",
  "postal_code",
  "state",
  "country",
  "emergency_contact_name",
  "emergency_contact_phone",
];

const emptyForm = () => ({
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  gender: "",
  birth_date: "",
  nationality: "",
  id_document_type: "",
  id_document_number: "",
  disability_percentage: "",
  personal_phone: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  postal_code: "",
  state: "",
  country: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
});

const fromApi = (profile, personal) => {
  const out = emptyForm();
  if (profile) {
    out.first_name = profile.first_name || "";
    out.last_name = profile.last_name || "";
    out.email = profile.email || "";
    out.phone = profile.phone || "";
  }
  if (personal) {
    PERSONAL_KEYS.forEach((k) => {
      const v = personal[k];
      out[k] = v === null || v === undefined ? "" : String(v);
    });
  }
  return out;
};

const toProfilePayload = (form) => ({
  first_name: form.first_name.trim(),
  last_name: form.last_name.trim(),
  phone: form.phone.trim() || null,
});

const toPersonalPayload = (form) => {
  const payload = {};
  PERSONAL_KEYS.forEach((k) => {
    const raw = (form[k] ?? "").toString().trim();
    if (raw === "") {
      payload[k] = null;
    } else if (k === "disability_percentage") {
      const num = Number(raw);
      payload[k] = Number.isFinite(num) ? num : null;
    } else {
      payload[k] = raw;
    }
  });
  return payload;
};

const PersonalDetailsModal = ({
  open,
  employeeProfileId,
  onClose,
  onSaved,
}) => {
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const totalSteps = STEPS.length;
  const isFirstStep = step === 0;
  const isLastStep = step === totalSteps - 1;

  const load = useCallback(async () => {
    if (!employeeProfileId) return;
    setLoading(true);
    setError(null);
    try {
      const [empRes, pdRes] = await Promise.all([
        getEmployee(employeeProfileId).catch(() => ({ data: null })),
        getEmployeePersonalDetails(employeeProfileId).catch(() => ({ data: null })),
      ]);
      setForm(fromApi(empRes?.data ?? null, pdRes?.data ?? null));
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los datos.");
    } finally {
      setLoading(false);
    }
  }, [employeeProfileId]);

  useEffect(() => {
    if (open) {
      setStep(0);
      load();
    }
  }, [open, load]);

  const handle = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const goNext = () => {
    if (isLastStep) return;
    setStep((s) => s + 1);
  };

  const goPrev = () => {
    if (isFirstStep) return;
    setStep((s) => s - 1);
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!employeeProfileId) return;
    setSaving(true);
    setError(null);
    try {
      const [empRes, pdRes] = await Promise.all([
        updateEmployee(employeeProfileId, toProfilePayload(form)),
        upsertEmployeePersonalDetails(employeeProfileId, toPersonalPayload(form)),
      ]);
      const profile = empRes?.data ?? empRes ?? null;
      const personal = pdRes?.data ?? pdRes ?? null;
      if (onSaved) onSaved({ profile, personal });
      toast.success("Datos guardados", "El empleado se actualizó correctamente.");
      onClose();
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.message || "No se pudieron guardar los datos.";
      setError(msg);
      toast.error("No se pudieron guardar los datos", msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Datos del empleado" size="xl">
      <form onSubmit={onSave}>
        <ModalBody>
          <StepIndicator step={step} steps={STEPS} />

          {error && (
            <div style={{ marginTop: 12, marginBottom: 12 }}>
              <Alert tone="danger">{error}</Alert>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spinner size={20} />
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
              {step === 0 && <StepGeneral form={form} handle={handle} />}
              {step === 1 && <StepPersonal form={form} handle={handle} />}
              {step === 2 && <StepContacto form={form} handle={handle} />}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          {!isFirstStep && (
            <Button variant="ghost" type="button" onClick={goPrev} disabled={saving}>
              Anterior
            </Button>
          )}
          {isLastStep ? (
            <Button
              key="submit"
              variant="primary"
              type="submit"
              disabled={saving || loading}
            >
              {saving && <Spinner size={12} color="#fff" style={{ marginRight: 6 }} />}
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          ) : (
            <Button
              key="next"
              variant="primary"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                goNext();
              }}
              disabled={loading}
            >
              Siguiente
            </Button>
          )}
        </ModalFooter>
      </form>
    </Modal>
  );
};

function StepIndicator({ step, steps }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {steps.map((s, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <React.Fragment key={s.id}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 600,
                  background: active || done ? "var(--kr-action)" : "var(--kr-neutral-100)",
                  color: active || done ? "#fff" : "var(--kr-fg-muted)",
                }}
              >
                {i + 1}
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: active ? 600 : 500,
                  color: active ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)",
                }}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <span
                style={{
                  flex: 1,
                  height: 1,
                  background: "var(--kr-border-subtle)",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StepGeneral({ form, handle }) {
  return (
    <SectionCard title="Información del empleado">
      <Grid2>
        <Field label="Nombre" required>
          <Input
            value={form.first_name}
            onChange={handle("first_name")}
            maxLength={100}
            required
            autoFocus
          />
        </Field>
        <Field label="Apellidos" required>
          <Input
            value={form.last_name}
            onChange={handle("last_name")}
            maxLength={120}
            required
          />
        </Field>
        <Field
          label="Email"
          help="Se gestiona desde la cuenta del usuario."
        >
          <Input value={form.email} onChange={() => {}} disabled />
        </Field>
        <Field label="Teléfono">
          <Input value={form.phone} onChange={handle("phone")} maxLength={30} />
        </Field>
      </Grid2>
    </SectionCard>
  );
}

function StepPersonal({ form, handle }) {
  return (
    <SectionCard title="Información personal">
      <Grid2>
        <Field label="Género">
          <Select value={form.gender} onChange={handle("gender")}>
            <option value="">Selecciona…</option>
            {GENDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Fecha de nacimiento">
          <Input type="date" value={form.birth_date} onChange={handle("birth_date")} />
        </Field>
        <Field label="Nacionalidad">
          <Select value={form.nationality} onChange={handle("nationality")}>
            <option value="">Selecciona…</option>
            {COUNTRIES.map((c) => (
              <option key={`nat-${c.code}`} value={c.name}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Tipo de documento">
          <Select value={form.id_document_type} onChange={handle("id_document_type")}>
            <option value="">Selecciona…</option>
            {ID_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Número de documento">
          <Input
            value={form.id_document_number}
            onChange={handle("id_document_number")}
            maxLength={60}
          />
        </Field>
        <Field label="% Discapacidad">
          <Input
            type="number"
            value={form.disability_percentage}
            onChange={handle("disability_percentage")}
            min={0}
            max={100}
            step="0.01"
          />
        </Field>
      </Grid2>
    </SectionCard>
  );
}

function StepContacto({ form, handle }) {
  return (
    <SectionCard title="Contacto">
      <Grid2>
        <Field label="Teléfono personal">
          <Input
            value={form.personal_phone}
            onChange={handle("personal_phone")}
            maxLength={30}
          />
        </Field>
        <div />
        <Field label="Dirección 1">
          <Input value={form.address_line_1} onChange={handle("address_line_1")} />
        </Field>
        <Field label="Dirección 2">
          <Input value={form.address_line_2} onChange={handle("address_line_2")} />
        </Field>
        <Field label="Ciudad">
          <Input value={form.city} onChange={handle("city")} />
        </Field>
        <Field label="Código postal">
          <Input value={form.postal_code} onChange={handle("postal_code")} maxLength={20} />
        </Field>
        <Field label="Estado / Provincia / Región">
          <Input value={form.state} onChange={handle("state")} />
        </Field>
        <Field label="País">
          <Select value={form.country} onChange={handle("country")}>
            <option value="">Selecciona…</option>
            {COUNTRIES.map((c) => (
              <option key={`co-${c.code}`} value={c.name}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
      </Grid2>
      <div style={{ marginTop: 12 }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "var(--kr-fg-muted)",
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          Contacto de emergencia
        </div>
        <Grid2>
          <Field label="Nombre">
            <Input
              value={form.emergency_contact_name}
              onChange={handle("emergency_contact_name")}
            />
          </Field>
          <Field label="Teléfono">
            <Input
              value={form.emergency_contact_phone}
              onChange={handle("emergency_contact_phone")}
              maxLength={30}
            />
          </Field>
        </Grid2>
      </div>
    </SectionCard>
  );
}

function SectionCard({ title, children }) {
  return (
    <Card>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--kr-fg-strong)",
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      {children}
    </Card>
  );
}

function Grid2({ children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 14,
      }}
    >
      {children}
    </div>
  );
}

export default PersonalDetailsModal;
